import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field, FieldProps } from 'formik';
import { AiOutlineMail } from 'react-icons/ai';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { AuthLayout } from '../../../layouts';
import { forgotPasswordSchema } from '../../../../constants';
import { DAV_APIS } from '../../../../adapters';
import { Alert } from '../../../commons';

const ForgotPassword: FC = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const submitHandler = async (values: any) => {
    setLoading(true);
    try {
      await DAV_APIS.auth.forgotPassword(values.email);
      setLoading(false);
      setEmailSent(true);
      Alert({ message: 'Reset Link Sent Successfully', variant: 'success' });
    } catch (error) {
      setLoading(false);
      Alert({ message: 'Failed to generate reset link', variant: 'error' });
    }
  };

  // Success state after email is sent
  if (emailSent) {
    return (
      <AuthLayout title='Check Your Email' subtitle='We sent you a password reset link'>
        <div className='text-center space-y-6'>
          {/* Success icon */}
          <div className='mx-auto w-16 h-16 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center'>
            <svg 
              className='w-8 h-8 text-green-600 dark:text-green-400' 
              fill='none' 
              viewBox='0 0 24 24' 
              stroke='currentColor'
            >
              <path 
                strokeLinecap='round' 
                strokeLinejoin='round' 
                strokeWidth={2} 
                d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' 
              />
            </svg>
          </div>

          <div className='space-y-2'>
            <p className='text-gray-600 dark:text-gray-400'>
              If an account exists with that email, you will receive a password reset link shortly.
            </p>
            <p className='text-sm text-gray-500'>
              Didn&apos;t receive the email? Check your spam folder.
            </p>
          </div>

          <Link
            to='/'
            className='
              w-full h-12 
              flex items-center justify-center gap-2
              text-white font-semibold
              rounded-xl
              shadow-lg
              transition-all duration-200
              hover:opacity-90
            '
            style={{
              background: 'linear-gradient(to right, #10B981, #059669)',
            }}
          >
            <HiOutlineArrowLeft className='w-5 h-5' />
            Back to Login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout 
      title='Forgot Password?' 
      subtitle='No worries, we&apos;ll send you reset instructions'
    >
      <Formik
        enableReinitialize
        initialValues={{
          email: '',
        }}
        validationSchema={forgotPasswordSchema}
        onSubmit={async (values, { resetForm }) => {
          await submitHandler(values);
          resetForm();
        }}
      >
        {() => (
          <Form className='space-y-5'>
            {/* Email Field */}
            <Field name='email'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <label 
                    htmlFor='email' 
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Email Address
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
                      <AiOutlineMail className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      {...field}
                      id='email'
                      type='email'
                      autoComplete='email'
                      placeholder='you@example.com'
                      className={`
                        w-full h-12 pl-12 pr-4 
                        bg-gray-50 dark:bg-gray-800 
                        border rounded-xl
                        text-gray-900 dark:text-white
                        placeholder:text-gray-400
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                        ${meta.touched && meta.error ?
                          'border-red-500'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}
                      `}
                    />
                  </div>
                  {meta.touched && meta.error && (
                    <p className='text-sm text-red-500 flex items-center gap-1'>
                      <span className='inline-block w-1 h-1 rounded-full bg-red-500' />
                      {meta.error}
                    </p>
                  )}
                </div>
              )}
            </Field>

            {/* Submit Button */}
            <button
              type='submit'
              disabled={loading}
              style={{
                background: 'linear-gradient(to right, #10B981, #059669)',
              }}
              className='
                w-full h-12 
                text-white font-semibold
                rounded-xl
                shadow-lg
                transition-all duration-200
                hover:opacity-90
                hover:-translate-y-0.5
                active:translate-y-0
                disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0
                flex items-center justify-center gap-2
              '
            >
              {loading ? (
                <>
                  <svg 
                    className='animate-spin h-5 w-5' 
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <circle 
                      className='opacity-25' 
                      cx='12' 
                      cy='12' 
                      r='10' 
                      stroke='currentColor' 
                      strokeWidth='4'
                    />
                    <path 
                      className='opacity-75' 
                      fill='currentColor' 
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span>Sending...</span>
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>

            {/* Back to Login */}
            <Link
              to='/'
              className='
                w-full h-12 
                flex items-center justify-center gap-2
                text-gray-600 dark:text-gray-400
                font-medium
                rounded-xl
                transition-all duration-200
                hover:bg-gray-100 dark:hover:bg-gray-800
              '
            >
              <HiOutlineArrowLeft className='w-5 h-5' />
              Back to Login
            </Link>

            {/* Create account link */}
            <div className='text-center text-sm text-gray-500'>
              Don&apos;t have an account?{' '}
              <Link 
                to='/register' 
                className='text-primary-600 font-medium hover:text-primary-700'
              >
                Sign up
              </Link>
            </div>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default ForgotPassword;
